import { validateProps, optInProps } from "../../common/types";
import { LogonParams } from "../../redux/slices/types";
import { setInputErrors } from "../../redux/slices/authSlice";

export default function validate(values: validateProps) {
  let errors = {} as validateProps;

  return errors;
}

export const validateTextLength = (value: string, key: string, min?: number, max?: number) => {
  let errors = {} as any;
  
  // If min or max is provided but no value, show min length error
  if (!value && (min !== undefined || max !== undefined)) {
    errors[key] = min 
      ? `This field must be at least ${min} characters`
      : `This field must not be empty`;
    return errors;
  }

  if (min !== undefined && value.length < min) {
    errors[key] = `This field must be at least ${min} characters`;
  }
  
  if (max !== undefined && value.length > max) {
    errors[key] = `This field must be less than ${max} characters`;
  }

  return errors;
}

export const validateRequired = (value: string) => {
  let errors = {} as any;
  if (!value) errors.required = "This field is required";
  return errors;
}

// Opt-in form validation
export function validateOptInProps(values: any) {
  let errors = {} as optInProps;

  // Get values using type-safe approach
  const firstName = values.firstName || values.first_name || '';
  const lastName = values.lastName || values.last_name || '';
  const phoneNumber = values.phoneNumber || values.msisdn || '';
  const region = values.region || values.region_id || '';
  const county = values.county || values.county_id || '';
  
  // Handle areas as an array
  const areas = values.areas || values.area_ids || values.area_id || values.area || [];
  const hasAreas = Array.isArray(areas) ? areas.length > 0 : Boolean(areas);

  if (!firstName) {
    errors.firstName = "First name is required";
  }

  if (!lastName) {
    errors.lastName = "Last name is required";
  }

  if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
    errors.email = "Email is invalid";
  }

  if (!phoneNumber) {
    errors.phoneNumber = "Phone number is required";
  } else if (!validatePhoneNumber(phoneNumber)) {
    errors.phoneNumber = "Please enter a valid number";
  }

  if (values.password !== undefined && !values.password) {
    errors.password = "Password is required";
  } else if (values.password && values.password.length < 6) {
    errors.password = "Password must be at least 6 characters";
  } else if (values.password && (!/\d/.test(values.password) || !/[a-zA-Z]/.test(values.password))) {
    errors.password = "Password must have a letter and a number";
  }

  if (!region) {
    errors.region = "Region is required";
  }

  if (!county) {
    errors.county = "County is required";
  }

  // Only set areas error if no areas are selected
  if (!hasAreas) {
    errors.areas = "Please select at least one area";
  }
  // Note: If areas are selected, errors.areas will be undefined, which will be treated as null
  
  return errors;
}

export function validateReportOutage(values: any) {
  let errors = {} as any;

  if (!values.region) {
    errors.region = "Region is required";
  }
  if (!values.county) {
    errors.county = "County is required";
  }
  if (!values.area) {
    errors.area = "Area is required";
  }
  if (!values.outagePhoneNumber) {
    errors.outagePhoneNumber = "Phone number is required";
  } else if (!/^(?:\+?254|0)?7|1\d{8}$/.test(values.outagePhoneNumber)) {
    errors.outagePhoneNumber = "Phone number is invalid";
  }
  if (!values.meterNumber) {
    errors.meterNumber = "Meter number is required";
  } else if (values.meterNumber.length < 8) {
    errors.meterNumber = "Meter number must be at least 8 characters";
  }
  return errors;
}

export function validateLogon(values: LogonParams, key?: keyof LogonParams) {
  let errors = {
    phoneNumber: null,
    password: null,
  } as LogonParams;

  // Function to validate phone number
  const validatePhoneNumber = () => {
    if (!values.phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (!/^(?:\+?254|0)?7|1\d{8}$/.test(values.phoneNumber)) {
      errors.phoneNumber = "Phone number is invalid";
    }
  };

  // Function to validate password
  const validatePassword = () => {
    if (!values.password) {
      errors.password = "Password is required";
    } else if (values.password.length <= 4) {
      errors.password = "Password must be at least 4 characters";
    }
  };

  // Mapping of keys to their respective validation functions
  const validationMap: { [K in keyof LogonParams]?: () => void } = {
    phoneNumber: validatePhoneNumber,
    password: validatePassword,
  };

  if (key) {
    // Execute the validation function for the given key if it exists
    validationMap[key]?.();
  } else {
    // Validate all keys
    Object.values(validationMap).forEach(validateFn => validateFn?.());
  }

  return errors;
}

export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove any non-digit characters except plus sign
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Remove leading '+254' or '254' or '0'
  if (cleaned.startsWith('+254')) {
    cleaned = cleaned.substring(4);
  } else if (cleaned.startsWith('254')) {
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }
  
  return cleaned;
};

export const validatePhoneNumber = (phone: string): boolean => {
  const cleaned = formatPhoneNumber(phone);
  // Check if it's a valid 9-digit number
  return /^7\d{8}$/.test(cleaned);
};

